import type { CheckoutFormFieldKey } from "@app/types/checkout-form-types";
import type {
  CheckoutStep,
  ConfirmationSource,
  PurchaseStep
} from "@app/types/checkout-types";
import type {
  CreditCardBrand,
  PaymentType,
  PublicState
} from "@app/types/napi-types";

export const PURCHASE_COMPLETE_REDIRECT_TIMEOUT_IN_MS = 1000;
export const PURCHASE_POLL_INTERVAL_IN_MS = 1000;
// Heroku closes the connection after 30s
// On April 2020, 99.9% of `/purchases/puchase_id/status` requests are completed under 5s
export const PURCHASE_MAX_POLL_TIME_IN_MS = 30 * 1000;

export const PURCHASE_STATES: { [k: string]: PublicState } = {
  VALIDATE: "validate",
  RESERVE: "reserve",
  AUTHORIZE: "authorize",
  REDIRECT_USER: "redirect",
  IDENTIFY_USER: "identify",
  DERISK: "derisk",
  BOOK: "book",
  COMPLETE: "complete",
  CAPTURE: "capture",
  DELAYED: "delayed",
  FAILED: "failed",
  ERROR: "error",
  ACTION_REQUIRED: "action_required"
};

export const PURCHASE_STEPS_NAMES: { [key: string]: PurchaseStep } = {
  NOT_STARTED: "not_started",
  VALIDATE: "validate",
  RESERVE: "reserve",
  AUTHORIZE: "authorize",
  DERISK: "derisk",
  BOOK: "book",
  COMPLETED: "completed"
};

export const PURCHASE_ACTIONS = {
  RETRY: "retry"
};

export const PASSENGER_CATEGORIES = {
  ADULT: "adult",
  CHILD: "child",
  SENIOR: "senior"
};

export const PAYMENT_TYPES: { [k: string]: PaymentType } = {
  CREDIT_CARD: "credit-card",
  INTERAC: "interac",
  SOFORT: "sofort",
  KLARNA_PAY_NOW: "klarna-pay-now",
  KLARNA_PAY_LATER: "klarna-pay-later",
  TRUSTLY: "trustly",
  IDEAL: "ideal",
  METRO_TURIZM: "metro-turizm",
  STITCH_INSTANT_PAY: "stitch-instant-pay"
};

export const PAYMENT_PLATFORMS = {
  SPREEDLY: "spreedly",
  METRO_TURIZM: "external_metro_turizm",
  ADYEN: "adyen",
  STITCH: "stitch",
  YUNO: "yuno",
  METRO_TURIZM_V2: "metro-turizm",
  GENERIC_REDIRECT: "generic-redirect"
} as const;

export const CREDIT_CARD_BRANDS: { [k: string]: CreditCardBrand } = {
  AMERICAN_EXPRESS: "american_express",
  CHINA_UNION_PAY: "china_union_pay",
  DINERS: "diners",
  DISCOVER: "discover",
  ELO: "elo",
  HIPERCARD: "hipercard",
  JCB: "jcb",
  MAESTRO: "maestro",
  MASTERCARD: "master",
  VISA: "visa"
};

export const CHECKOUT_STEPS: Record<"PASSENGERS" | "PAYMENT", CheckoutStep> = {
  PASSENGERS: "passengers",
  PAYMENT: "payment"
};

export const PASSENGER_QUESTION_TYPE = {
  BOOLEAN: "boolean",
  DATE: "date",
  SELECTION: "selection",
  TEXT: "text"
};

export const CONFIRMATION_SOURCE: { [k: string]: ConfirmationSource } = {
  REDIRECT: "redirect",
  THREEDS: "3ds",
  EXTERNAL_PARTNER_PAYMENT: "external_partner_payment"
};

export const COOKIE_UNSAFE_PASSENGER_QUESTIONS_KEYS: CheckoutFormFieldKey[] = [
  "government_id",
  "id_number",
  "id_type"
];

/** @see https://docs.google.com/spreadsheets/d/1zlmrL-mz7c5apoDVDPq39OFwhrby130Lpft4vjexmKI */
export const EXPLICIT_OPT_IN_COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CA",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "GB",
  "IS",
  "NO",
  "TR",
  "AU",
  "JP",
  "KR",
  "TH",
  "BR"
];
